import { ImageSegmenter, FilesetResolver, ImageSegmenterResult } from '@mediapipe/tasks-vision'
import type { ClockRequest, ClockTick } from './filterClock.worker'

export type BackgroundFilterMode = 'none' | 'blur' | 'hide'

// RISKS.md/P1.18: same-origin on purpose, because the CSP blocks a CDN URL here. The wasm is copied in
// by scripts/copy-mediapipe-wasm.mjs.
const WASM_PATH = '/mediapipe/wasm'
const MODEL_PATH = '/mediapipe/models/selfie_segmenter.tflite'
// Long enough for a genuine start on a slow machine, short enough that a dead source cannot stall
// the caller's promise chain (RISKS.md/P2.38).
const PLAY_TIMEOUT_MS = 3000
const FRAME_RATE = 30
const FRAME_INTERVAL_MS = 1000 / FRAME_RATE

// RISKS.md/P2.31: Firefox has requestFrame() on the stream, not the track, so keep both branches.
export function frameRequester(stream: MediaStream): (() => void) | null {
  const track = stream.getVideoTracks()[0] as Partial<CanvasCaptureMediaStreamTrack> | undefined
  if (track && typeof track.requestFrame === 'function') {
    const request = track.requestFrame
    return () => request.call(track)
  }
  const legacyStream = stream as MediaStream & { requestFrame?: () => void }
  if (typeof legacyStream.requestFrame === 'function') {
    const request = legacyStream.requestFrame
    return () => request.call(legacyStream)
  }
  return null
}

// RISKS.md/P1.18: the `!` keeps this notice in the built bundle.
/*! @mediapipe/tasks-vision is Apache-2.0 licensed. Licence and third-party notices: /mediapipe/THIRD_PARTY_NOTICES.txt */
export class BackgroundFilter {
  private segmenter: ImageSegmenter | null = null
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private videoEl: HTMLVideoElement | null = null
  private mode: BackgroundFilterMode = 'none'
  private running = false
  private initPromise: Promise<void> | null = null
  private filteredStream: MediaStream | null = null
  private requestFrame: (() => void) | null = null
  private lastTimestamp = -1
  private worker: Worker | null = null
  private session = 0
  private warned = false
  // RISKS.md/P2.31: allocate lazily, never in the constructor, which MediaContext runs every render.
  private tmp: HTMLCanvasElement | null = null
  private maskCanvas: HTMLCanvasElement | null = null
  private maskImageData: ImageData | null = null

  constructor() {
    this.canvas = document.createElement('canvas')
    this.ctx = this.canvas.getContext('2d')!
  }

  // RISKS.md/P2.29: cache the in-flight PROMISE — a boolean flag cannot work here, because it
  // would only be set after two awaited downloads, so concurrent apply() calls sail past the
  // guard and each builds its own ImageSegmenter (WASM heap + GPU delegate) that nothing closes.
  private async init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = (async () => {
        // No second argument: `true` loads the `_module_` wasm variant, which is not copied.
        const vision = await FilesetResolver.forVisionTasks(WASM_PATH)
        this.segmenter = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_PATH,
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        })
      })().catch(e => {
        // Let the next call retry rather than wedging the filter forever on one bad network moment.
        this.initPromise = null
        throw e
      })
    }
    return this.initPromise
  }

  // RISKS.md/P2.31: a worker clock, because a hidden page stops rAF and throttles main-thread timers.
  private ensureWorker(): void {
    if (this.worker) return
    // RISKS.md/P2.4: a classic worker fails under the dev server, so the filter draws nothing.
    this.worker = new Worker(new URL('./filterClock.worker.ts', import.meta.url), { type: 'module' })
    this.worker.onmessage = this.onTick
    this.worker.onerror = e => console.warn('background filter clock failed:', e.message)
  }

  private schedule(delayMs: number): void {
    const request: ClockRequest = { type: 'schedule', session: this.session, delayMs }
    this.worker?.postMessage(request)
  }

  private onTick = (e: MessageEvent<ClockTick>): void => {
    // RISKS.md/P2.31: a tick queued before stop() may land after the next apply() sets `running`.
    if (!this.running || e.data.session !== this.session) return
    const started = performance.now()
    try {
      this.processFrame()
    } catch (err) {
      if (!this.warned) {
        this.warned = true
        console.warn('background filter frame failed:', err)
      }
      // RISKS.md/P2.31: an opaque frame, never the frozen last picture and never the raw camera.
      this.paintOpaqueBackground()
      this.requestFrame?.()
    } finally {
      this.schedule(Math.max(0, FRAME_INTERVAL_MS - (performance.now() - started)))
    }
  }

  private processFrame(): void {
    if (!this.videoEl || !this.segmenter) return
    if (this.videoEl.readyState < 2) return

    const now = performance.now()
    if (now === this.lastTimestamp) return
    this.lastTimestamp = now

    this.segmenter.segmentForVideo(this.videoEl, now, (result: ImageSegmenterResult) => {
      this.applyMask(result)
    })
    // RISKS.md/P2.31: not inside applyMask, whose no-mask early return draws too.
    this.requestFrame?.()
  }

  private paintOpaqueBackground(): void {
    // RISKS.md/P2.31: a throw between the blur branch's save() and restore() leaves its filter set.
    this.ctx.filter = 'none'
    this.ctx.fillStyle = '#212629'
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
  }

  private sizedCanvas(current: HTMLCanvasElement | null, width: number, height: number): HTMLCanvasElement {
    const canvas = current ?? document.createElement('canvas')
    if (canvas.width !== width) canvas.width = width
    if (canvas.height !== height) canvas.height = height
    return canvas
  }

  private maskBuffer(width: number, height: number): ImageData {
    let buffer = this.maskImageData
    if (!buffer || buffer.width !== width || buffer.height !== height) {
      buffer = new ImageData(width, height)
      this.maskImageData = buffer
    }
    return buffer
  }

  private applyMask(result: ImageSegmenterResult): void {
    const { width, height } = this.canvas
    const ctx = this.ctx
    // RISKS.md/P2.29: do NOT replace this with a `!` assertion. This runs in a synchronous
    // segmentForVideo callback, so a stop() between the frame request and this call nulls videoEl
    // mid-flight; throwing here skips confidenceMask.close() (a WASM buffer per frame) and never
    // reschedules, freezing the filter permanently.
    const video = this.videoEl
    if (!video) {
      result.confidenceMasks?.[0]?.close()
      return
    }

    const confidenceMask = result.confidenceMasks?.[0]
    if (!confidenceMask) {
      // Fallback: draw video directly if no mask
      ctx.drawImage(video, 0, 0, width, height)
      return
    }

    const maskData = confidenceMask.getAsFloat32Array()
    const maskWidth = confidenceMask.width
    const maskHeight = confidenceMask.height

    // Step 1: Draw background (blurred or solid color)
    ctx.clearRect(0, 0, width, height)
    if (this.mode === 'blur') {
      ctx.save()
      ctx.filter = 'blur(18px)'
      ctx.drawImage(video, 0, 0, width, height)
      ctx.restore()
    } else {
      this.paintOpaqueBackground()
    }

    // Step 2: Build RGBA mask ImageData — person pixels opaque, background transparent
    // maskData values: 0.0 = background, 1.0 = person
    const maskImageData = this.maskBuffer(maskWidth, maskHeight)
    const alpha = maskImageData.data
    for (let i = 0; i < maskData.length; i++) {
      alpha[i * 4 + 3] = Math.round(maskData[i] * 255)
    }

    // Step 3: Draw unblurred person on temp canvas using the mask
    const tmp = this.tmp = this.sizedCanvas(this.tmp, width, height)
    const tctx = tmp.getContext('2d')!

    // Draw clear video
    tctx.drawImage(video, 0, 0, width, height)

    // Apply mask: keep only person pixels
    const maskCanvas = this.maskCanvas = this.sizedCanvas(this.maskCanvas, maskWidth, maskHeight)
    maskCanvas.getContext('2d')!.putImageData(maskImageData, 0, 0)

    tctx.globalCompositeOperation = 'destination-in'
    tctx.drawImage(maskCanvas, 0, 0, width, height)
    tctx.globalCompositeOperation = 'source-over'

    // Step 4: Composite person over background
    ctx.drawImage(tmp, 0, 0, width, height)

    confidenceMask.close()
  }

  // RISKS.md/P1.24: a released chain can let a stale caller resume here after a newer apply() or
  // stop() has taken over, so every await is followed by a session check. A stale run answers
  // `null`, never `inputStream` — under blur or hide that is the RAW camera, which the caller
  // publishes on sight (RISKS.md/P2.31 forbids it).
  async apply(inputStream: MediaStream, mode: BackgroundFilterMode): Promise<MediaStream | null> {
    this.stop()
    this.mode = mode

    if (mode === 'none') return inputStream

    // ⚠ `stop()` above is the only thing that bumps the session — move it (RISKS.md/P2.62) and the
    // checks below stop telling runs apart.
    const session = this.session

    await this.init()
    if (this.session !== session) return null

    const track = inputStream.getVideoTracks()[0]
    if (!track) return inputStream

    const settings = track.getSettings()
    this.canvas.width = settings.width || 640
    this.canvas.height = settings.height || 480

    this.videoEl = document.createElement('video')
    this.videoEl.srcObject = new MediaStream([track])
    this.videoEl.autoplay = true
    this.videoEl.muted = true
    this.videoEl.playsInline = true
    // RISKS.md/P2.38: MUST be time-boxed. `play()` rejects only on an error, so a source track that
    // dies while it is pending leaves it never settling at all — the `.catch` cannot help. The
    // chain's own 30s release (RISKS.md/P1.24) is not a substitute: that abandons the whole run and
    // leaves the preview empty, where this lets the filter still start.
    await Promise.race([
      this.videoEl.play().catch(() => {}),
      new Promise<void>(resolve => setTimeout(resolve, PLAY_TIMEOUT_MS)),
    ])

    // Before the liveness branch on purpose: its stop() would tear down the filter a NEWER session
    // is running, stopping a canvas track peers consume — which fires no `ended`, so nothing notices.
    if (this.session !== session) return null

    // May have died while we waited — don't build on it; the `ended` handler chains a proper teardown.
    if (track.readyState !== 'live') {
      this.stop()
      return inputStream
    }

    const captured = this.canvas.captureStream(0)
    const request = frameRequester(captured)
    if (request) {
      this.filteredStream = captured
      this.requestFrame = request
    } else {
      // RISKS.md/P2.31: unreached in current browsers; capture on change beats a track with no frames.
      captured.getTracks().forEach(t => t.stop())
      this.filteredStream = this.canvas.captureStream(FRAME_RATE)
    }
    inputStream.getAudioTracks().forEach(t => this.filteredStream!.addTrack(t))

    this.running = true
    this.warned = false
    this.ensureWorker()
    this.schedule(0)

    return this.filteredStream
  }

  stop(): void {
    this.running = false
    this.session++
    const request: ClockRequest = { type: 'stop' }
    this.worker?.postMessage(request)
    if (this.videoEl) {
      this.videoEl.pause()
      this.videoEl.srcObject = null
      this.videoEl = null
    }
    // RISKS.md/P2.29: VIDEO tracks only — NOT getTracks(). apply() copies the input's audio tracks
    // into this stream, so stopping those silently mutes the user's microphone. Stopping the video
    // ones is required: `captureStream()` mints a live track per apply() and a canvas track never
    // reports `ended`, so nothing downstream can clean it up.
    this.filteredStream?.getVideoTracks().forEach(t => t.stop())
    this.filteredStream = null
    this.requestFrame = null
  }

  destroy(): void {
    this.stop()
    this.worker?.terminate()
    this.worker = null
    this.segmenter?.close()
    this.segmenter = null
    this.initPromise = null
  }
}
